// Store-agnostic WS lobby connector (C3). The gather store passes callbacks; this file
// knows nothing about Vuex. Read-only stream: no client→server messages are ever sent
// (the browser answers protocol-level pings automatically). Mutations go over REST.
import config from '@/helpers/config';
import { wsLobbyUrl, nextBackoffMs } from '@/helpers/apicore/adapters';
import { lobbyProtocols } from '@/helpers/apicore';

// The client-visible message set (R19): snapshot + 8 deltas + ping. `refresh` is
// server-internal. Unknown types are ignored for forward compatibility.
const KNOWN_DELTAS = new Set([
  'player_joined',
  'player_left',
  'player_updated',
  'config_updated',
  'status_changed',
  'gather_cancelled',
  'votes_updated',
  'suggestions_updated',
]);

// getUserToken is a FUNCTION, not a captured value: it is re-read on every (re)connect
// so a token rotated or cleared mid-session is picked up, and no token string lingers
// in this long-lived closure.
export function createLobbySocket({ token, getUserToken, onSnapshot, onDelta, onDown, onUp }) {
  let ws = null;
  let attempt = 0;
  let closed = false;
  let reconnectTimer = null;
  let downFired = false;

  const clearReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (closed) return;
    clearReconnect();
    reconnectTimer = setTimeout(open, nextBackoffMs(attempt++));
  };

  function open() {
    if (closed) return;
    try {
      ws = new WebSocket(wsLobbyUrl(config.apicore.url, token), lobbyProtocols(getUserToken()));
    } catch (e) {
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      attempt = 0;
      // Server re-sends a fresh snapshot on connect — that IS the resync (no diffing).
      if (downFired) {
        downFired = false;
        if (typeof onUp === 'function') onUp();
      }
    };
    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      if (!msg || typeof msg.type !== 'string') return;
      if (msg.type === 'snapshot') {
        if (typeof onSnapshot === 'function') onSnapshot(msg.data);
        return;
      }
      if (KNOWN_DELTAS.has(msg.type) && typeof onDelta === 'function') {
        onDelta(msg.type, msg.data);
      }
    };
    ws.onerror = () => {
      // onclose fires right after and owns the reconnect; nothing to do here.
    };
    ws.onclose = () => {
      if (closed) return;
      if (!downFired) {
        downFired = true;
        if (typeof onDown === 'function') onDown();
      }
      scheduleReconnect();
    };
  }

  open();

  return {
    close() {
      closed = true;
      clearReconnect();
      if (ws) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        try {
          ws.close();
        } catch (e) {
          // ignore — socket may not be open yet
        }
        ws = null;
      }
    },
  };
}
